'use client';

import React, { useState } from 'react';
import { trackEvent } from '@/lib/amplitude';

export type Faq = { question: string; answer: string };

/**
 * Figma desktop 303:3495 / mobile 303:3095.
 *
 * The questions in Figma are placeholders about ornaments (and one is duplicated), so the
 * copy here is the live homepage's comic FAQ, per the spec's content-sourcing rule.
 */
export default function FaqSection({ faqs }: { faqs: Faq[] }) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section id="faq" className="w-full border-t border-[var(--hp-hairline)] bg-white py-[var(--hp-section-y)] dt:border-t-0 dt:bg-white dt:py-20">
      <div className="hp-container">
        <h2
          className="text-center font-bold tracking-[-0.454px] text-[var(--hp-ink)]"
          style={{ fontSize: 'var(--hp-h2)', lineHeight: 1.35 }}
        >
          Frequent questions
        </h2>

        {/* Mobile: 10px-gapped #eeeff1 cards. Desktop: flush rows separated by a rule. */}
        <div className="mx-auto mt-8 flex max-w-[684px] flex-col gap-[10px] dt:mt-12 dt:gap-0">
          {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            return (
              <div
                key={faq.question}
                className="overflow-hidden rounded-[12px] bg-[var(--hp-faq-card)] dt:rounded-none dt:border-t dt:border-[var(--hp-faq-border)]"
              >
                <button
                  id={`btn-faq-${index}`}
                  type="button"
                  onClick={() => {
                    trackEvent('FAQ - Question Toggled', { index, question: faq.question, open: !isOpen });
                    setOpenFaq(isOpen ? null : index);
                  }}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${index}`}
                  className="group flex w-full cursor-pointer items-center justify-between gap-3 py-[18px] pl-5 pr-6 text-left dt:px-2 dt:py-[25px]"
                >
                  <span
                    className="font-medium text-[var(--hp-faq-q)]"
                    style={{ fontSize: 'var(--hp-faq-q-size)', lineHeight: 'var(--hp-faq-q-lh)' }}
                  >
                    {faq.question}
                  </span>
                  <svg
                    className={`size-5 shrink-0 text-[var(--hp-faq-q)] transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div id={`faq-panel-${index}`} className="hp-faq-panel" data-open={isOpen} role="region">
                  <div>
                    {/* whitespace-pre-line so the newline in the "digital version" answer survives.
                        Answer ink matches the question (--hp-faq-q) rather than the lighter grey. */}
                    <p
                      className="whitespace-pre-line pb-4 pl-5 pr-3 text-[var(--hp-faq-q)] dt:px-2 dt:pb-6"
                      style={{ fontSize: '16px', lineHeight: 1.6 }}
                    >
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
